import type { ReactNode } from 'react';
import { LogoMark } from './ui/Logo';

/** Provider-free app frame: also safe after the root error boundary unmounts auth/router. */
export function AppLoadingShell({
  children,
  accountAction,
  title,
  skeleton = true,
}: {
  children?: ReactNode;
  accountAction?: ReactNode;
  title?: ReactNode;
  /** False drops the placeholder cards under the content, so a final message (a refusal) does not read as still loading. */
  skeleton?: boolean;
}) {
  const loading = !children;
  return (
    <div className="min-h-screen flex bg-[var(--color-bg)]" data-testid="app-loading-shell">
      <aside className="hidden md:flex w-sidebar-collapsed shrink-0 flex-col items-center bg-white" aria-hidden="true">
        <div className="h-[72px] flex items-center"><LogoMark className="w-6 h-6" /></div>
        <div className="space-y-5 pt-3">
          {Array.from({ length: 7 }, (_, i) => <div key={i} className="w-6 h-6 rounded bg-gray-200" />)}
        </div>
      </aside>
      <main className="min-w-0 flex-1 app-ground md:border-l border-gray-200">
        <header className="min-h-[72px] px-4 md:px-8 py-3 flex items-center justify-between gap-3">
          <div className="flex flex-1 items-center gap-3 min-w-0">
            <LogoMark className="w-6 h-6 shrink-0 md:hidden" />
            {/* The title is the one element that looked loaded while the session was still in flight
                (Emily, 2026-10-10: "it should also be a skeleton"). Only a caller with content
                (failure, handoff) or a named title gets the wordmark. */}
            {title === undefined && loading
              ? <div data-testid="app-skeleton-title" aria-hidden="true" className="h-6 w-32 rounded skeleton-shimmer" />
              : <h1 className="page-title truncate">{title ?? 'OverlayQA'}</h1>}
          </div>
          {accountAction && <div className="shrink-0 flex items-center">{accountAction}</div>}
        </header>
        {/* Screen-reader-only, and outside the section so the skeleton sits flush at the top like real page content (Emily, 2026-10-09: no visible loading text). */}
        {loading && <p role="status" className="sr-only">Loading your account...</p>}
        <section className="page-content space-y-5">
          {children}
          {skeleton && <div
            aria-hidden="true"
            data-testid="app-skeleton-content"
            className={loading ? 'space-y-5 motion-reduce:[&_.skeleton-shimmer]:animate-none' : 'space-y-5 [&_.skeleton-shimmer]:animate-none'}
          >
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {Array.from({ length: 3 }, (_, i) => (
                <div key={i} className="card-sm space-y-4">
                  <div className="h-3 w-24 max-w-full rounded skeleton-shimmer" />
                  <div className="h-6 w-16 rounded skeleton-shimmer" />
                  <div className="h-3 w-2/3 rounded skeleton-shimmer" />
                </div>
              ))}
            </div>
            <div className="card-sm space-y-6">
              <div className="h-4 w-1/3 rounded skeleton-shimmer" />
              {Array.from({ length: 4 }, (_, i) => (
                <div key={i} className="flex gap-4 items-center">
                  <div className="w-9 h-9 rounded skeleton-shimmer shrink-0" />
                  <div className="h-3 w-1/2 rounded skeleton-shimmer" />
                  <div className="h-5 w-16 rounded skeleton-shimmer ml-auto" />
                </div>
              ))}
            </div>
          </div>}
        </section>
      </main>
    </div>
  );
}
